import { useCallback, useEffect, useMemo, useState } from 'react';
import './Printers.css';
import BasicCli from '../../main/components/basicCli/BasicCli';
import EditModal from '../../main/components/modal/edit/EditModal';
import DeleteModal from '../../main/components/modal/delete/DeleteModal';
import eFormType from '../../main/assets/enums/eFormType';
import { ApiConstants } from '../../main/constants/ApiConstants';
import axiosInstance from '../../main/initializers/axios';
import { toast } from 'react-toastify';
import GeneralTable from '../../main/components/grid/GeneralTable';
import IGridItems from '../../main/interfaces/IGridItems';
import IPagination from '../../main/interfaces/IPagination';
import PrinterEdit from './printerEdit/PrinterEdit';
import React from 'react';
import IPrinter from '../../main/interfaces/IPrinter';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import useGetEnums from '../../main/hooks/useGetEnums';

const Printers = () => {
  const allEnums = useGetEnums();
  const [items, setItems] = useState<IPrinter[]>([]);
  const [gridItems] = useState<IGridItems[]>([
    { entity: 'id', label: 'Id', isShown: true },
    { entity: 'printerName', label: 'Printer', isShown: true },
    { entity: 'operationLabel', label: 'Operation', isShown: true },
    { entity: 'isActive', label: 'Active', isShown: true },
    { entity: 'createdAt', label: 'Created', isShown: true },
  ] as any);

  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });

  const [tableName, setTableName] = useState('Printers');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState(false);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);

  const [selectedVal, setSelectedVal] = useState<IPrinter | null>(null);
  const [approved, setApproved] = useState(false);

  const { i18n } = useTranslation();

  const getPrinters = useCallback(async () => {
    try {
      const res = await axiosInstance.get(ApiConstants.utility.printers);
      setItems(res.data ?? []);
      setPagination((p) => ({ ...p, count: 1, page: 1 }));
    } catch {
      toast.error('Failed to fetch printers');
    }
  }, []);

  useEffect(() => {
    getPrinters();
  }, [getPrinters]);

  const handleEdit = (item: IPrinter) => {
    setTableName('Edit ' + (item.printerName ?? 'Printer'));
    setSelectedVal(item);
    setIsModalEditOpen(true);
  };

  const handleDelete = (item: IPrinter) => {
    setSelectedVal(item);
    setIsModalDeleteOpen(true);
  };

  const deleteDataSet = async (id: number) => {
    try {
      const res = await axiosInstance.delete(ApiConstants.utility.printersDelete, { params: { id } });
      if (res.status === 204 || res.status === 200) {
        toast.success(i18n.t('productCategory.deletedSuccessfully'));
        setIsModalDeleteOpen(false);
        getPrinters();
      } else {
        toast.error(i18n.t('productCategory.somethingWentWrong'));
      }
    } catch {
      toast.error(i18n.t('productCategory.somethingWentWrong'));
    }
  };

  const mapped = useMemo(
    () =>
      items.map((x) => ({
        ...x,
        id: x.id == null ? '' : `${x.id}`,
        operationLabel: (() => {
          const enumName = allEnums?.PrintingOperation?.find((item: any) => Number(item.id) === Number(x.operation))?.name;
          if (!enumName) {
            return x.operation == null ? '' : `#${x.operation}`;
          }

          return i18n.t(`enum.printingOperation.${enumName}`, { defaultValue: enumName });
        })(),
      })),
    [allEnums, i18n, items],
  );

  const downloadPrinterApp = async () => {
    try {
      const res = await axiosInstance.get(ApiConstants.print.printerApp, {
        responseType: 'blob',
      });

      const blob = new Blob([res.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'PrinterAL.exe';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(i18n.t('printers.downloadPrinterAppError', { defaultValue: 'Failed to download PrinterAL.exe' }));
    }
  };

  return (
    <BasicCli>
      <div className="prnWrap bcPageContent">
        <div className="prnContainer">
          <div className="bcPageHeader">
            <h1 className="bcPageTitle">{i18n.t('printers.printers')}</h1>
            <div className="bcPageHeaderActions">
              <button className="ui-control ui-btn" onClick={downloadPrinterApp} type="button">
                <DownloadIcon fontSize="small" />
                {i18n.t('printers.downloadPrinterApp', { defaultValue: 'Download Printer App' })}
              </button>
              <button className="ui-control ui-btn ui-btn--primary" onClick={() => setIsModalOpen(true)} type="button">
                {i18n.t('printers.addPrinters')}
              </button>
            </div>
          </div>

          <div className=" prnTable">
            <GeneralTable
              data={mapped}
              columns={gridItems}
              gridEntity={0}
              title=""
              hasRp={true}
              selectedItem={(val: any) => setSelectedVal(val)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              pagination={pagination}
              selectedItemPagination={(val: any) => setPagination(val)}
            />
          </div>

          {isModalDeleteOpen && selectedVal ? (
            <DeleteModal width="500px" handleClose={() => setIsModalDeleteOpen(false)} handleDelete={() => deleteDataSet(selectedVal.id ?? 0)}>
              {i18n.t('users.areYouSureYouWantToDelete?')} <strong className="text-danger">{selectedVal?.printerName}</strong> ?
            </DeleteModal>
          ) : null}

          {isModalEditOpen && selectedVal ? (
            <EditModal
              title={tableName}
              width="80%"
              closeModal={() => setIsModalEditOpen(false)}
              onSubmitData={() => setApproved(true)}
              setIsModalOpen={setIsModalEditOpen}
              modalType={eFormType.Edit}
            >
              <PrinterEdit
                tempPrinter={selectedVal}
                modalType={eFormType.Edit}
                createOrUpdate={approved}
                onSubmitData={() => {
                  getPrinters();
                  setApproved(false);
                  setIsModalEditOpen(false);
                }}
              />
            </EditModal>
          ) : null}

          {isModalOpen ? (
            <EditModal
              title={i18n.t('printers.addPrinters')}
              width="80%"
              closeModal={() => setIsModalOpen(false)}
              onSubmitData={() => setApproved(true)}
              setIsModalOpen={setIsModalOpen}
              modalType={eFormType.New}
            >
              <PrinterEdit
                modalType={eFormType.New}
                createOrUpdate={approved}
                onSubmitData={() => {
                  getPrinters();
                  setApproved(false);
                  setIsModalOpen(false);
                }}
              />
            </EditModal>
          ) : null}
        </div>
      </div>
    </BasicCli>
  );
};

export default Printers;
